import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Wrench, Package, Warning, ArrowRight, Clock, CaretDown, Phone, User, CalendarBlank, Tag, NotePencil, LockSimple, LockSimpleOpen, Plus, CheckCircle, Bell, WhatsappLogo } from "@phosphor-icons/react";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
const parseUtc = (d) => {
  const iso = typeof d === "string" && !d.endsWith("Z") && !d.includes("+") ? `${d}Z` : d;
  return new Date(iso).getTime();
};
const daysSince = (d) => {
  const dt = new Date(parseUtc(d));
  const start = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today - start) / 86400000));
};
const fmtDate = (d) => { const p = new Date(parseUtc(d)); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };
const fmtShort = (d) => { const [y, m, g] = String(d || "").split("-"); return y && m && g ? `${g}-${m}-${y.slice(2)}` : d; };

const AgeBadge = ({ date, testid }) => {
  const days = daysSince(date);
  if (days >= 2) {
    return (
      <span className="blink-red px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-500 border border-red-500/50 inline-flex items-center gap-1"
        data-testid={testid}>
        <Clock size={12} weight="bold" /> {days} gg in attesa
      </span>
    );
  }
  return (
    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-500 inline-flex items-center gap-1"
      data-testid={testid}>
      <Clock size={12} /> {days === 0 ? "oggi" : "1 gg"}
    </span>
  );
};

const Detail = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon size={15} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value}</p>
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [notif, setNotif] = useState({ unread: 0, items: [] });
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);
  const [credits, setCredits] = useState([]);
  const notesLoaded = useRef(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/cash/credits").then((r) => setCredits(r.data || [])).catch(() => {});
    api.get("/notes").then((r) => { setNotes(r.data.text); notesLoaded.current = true; }).catch(() => {});
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        const op = localStorage.getItem("active_operator");
        const { data: n } = await api.get("/notifications", { params: op ? { operator: op } : {} });
        setNotif({ unread: n.unread, items: n.items.filter((x) => !x.read) });
      } catch { /* ignore */ }
    };
    check();
    const iv = setInterval(check, 8000);
    window.addEventListener("operator-changed", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("operator-changed", check);
    };
  }, []);

  useEffect(() => {
    if (!notesLoaded.current) return;
    const t = setTimeout(async () => {
      try {
        await api.put("/notes", { text: notes });
        setNotesSaved(true);
        setTimeout(() => setNotesSaved(false), 1500);
      } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, [notes]);

  if (!data) {
    return (
      <div className="p-10 flex justify-center" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const toggle = (id) => setExpanded((prev) => (prev === id ? null : id));
  const doneRepairs = data.riparazioni.completate_oggi || [];
  const doneOrders = data.ordini.ritirati_oggi || [];
  const lateTotal = (data.riparazioni.in_ritardo || 0) + (data.ordini.in_ritardo || 0);
  const counts = [
    { label: "Riparazioni in sospeso", value: data.riparazioni.in_lavorazione, color: "text-amber-500", testid: "count-riparazioni-sospeso", to: "/riparazioni" },
    { label: "In ritardo (+2 gg)", value: lateTotal, color: lateTotal > 0 ? "text-red-500" : "text-foreground", testid: "count-in-ritardo", alert: lateTotal > 0, to: "/riparazioni" },
    { label: "Riparazioni fatte oggi", value: data.riparazioni.completati_oggi, color: "text-green-500", testid: "count-fatte-oggi", to: "/riparazioni" },
    { label: "Riparazioni completate", value: data.riparazioni.completate_totali ?? 0, color: "text-foreground", testid: "count-riparazioni-totali", to: "/riparazioni" },
    { label: "Ordini da evadere", value: data.ordini.in_attesa, color: "text-blue-400", testid: "count-ordini-evadere", to: "/ordini" },
    { label: "Arrivati da ritirare", value: data.ordini.arrivati, color: "text-amber-500", testid: "count-da-ritirare", to: "/ordini" },
    { label: "Ordini ritirati", value: data.ordini.ritirati_totali ?? 0, color: "text-foreground", testid: "count-ordini-ritirati", to: "/ordini" },
  ];

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Lavori del negozio</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Dashboard</h1>
        </div>
        <Link to="/riparazioni?nuova=1" data-testid="dashboard-new-repair-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
          <Plus size={17} weight="bold" /> Nuova riparazione
        </Link>
      </div>

      {notif.unread > 0 && (
        <div className="border border-primary/50 bg-primary/10 rounded-lg p-5" data-testid="dashboard-notifications-alert">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Bell size={20} weight="duotone" className="text-primary" />
              <h2 className="font-heading font-bold text-primary" data-testid="dashboard-notifications-title">
                {notif.unread === 1 ? "Hai 1 notifica non letta" : `Hai ${notif.unread} notifiche non lette`}
              </h2>
            </div>
            <button onClick={() => window.dispatchEvent(new CustomEvent("open-notifications"))} data-testid="dashboard-open-notifications"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity duration-150">
              Apri il pannello notifiche <ArrowRight size={14} />
            </button>
          </div>
          <ul className="mt-3 space-y-1.5">
            {notif.items.slice(0, 3).map((n) => (
              <li key={n.id} className="text-sm" data-testid={`dashboard-notification-${n.id}`}>
                <span className="font-semibold">{n.title}</span>
                <span className="text-muted-foreground"> — {n.body}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {credits.length > 0 && (
        <Link to="/prima-nota" className="block border border-amber-500/40 bg-amber-500/10 rounded-lg p-5 hover:bg-amber-500/15 transition-colors duration-150" data-testid="credits-alert">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Warning size={20} weight="duotone" className="text-amber-500" />
              <h2 className="font-heading font-bold text-amber-500" data-testid="credits-alert-title">
                {credits.length === 1 ? "1 credito da incassare" : `${credits.length} crediti da incassare`} per {eur(credits.reduce((s, c) => s + (c.balance_due || 0), 0))}
              </h2>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-500">
              Vai alla Prima Nota <ArrowRight size={14} />
            </span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {credits.slice(0, 3).map((c) => (
              <li key={c.id} className="text-sm" data-testid={`credits-alert-item-${c.id}`}>
                <span className="font-semibold">{c.description || c.category}</span>
                <span className="text-muted-foreground"> — {fmtShort(c.date)} · {eur(c.balance_due)}</span>
              </li>
            ))}
          </ul>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="work-counts">
        {counts.map((c) => (
          <Link key={c.label} to={c.to} data-testid={c.testid}
            className={`group border rounded-lg bg-card p-4 transition-colors duration-150 hover:bg-accent/60 ${c.alert ? "border-red-500/60 blink-red" : "border-border hover:border-foreground/30"}`}>
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2 leading-tight flex items-start justify-between gap-1">
              {c.label}
              <ArrowRight size={12} className="mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
            </p>
            <p className={`font-heading text-3xl font-extrabold ${c.color}`}>{c.value}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {data.ordini.da_ritirare.length > 0 && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-5" data-testid="arrived-orders-alert">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="duotone" className="text-amber-500" />
              <h2 className="font-heading font-bold text-amber-500">Ricambi arrivati da ritirare ({data.ordini.arrivati})</h2>
            </div>
            <ul className="space-y-1.5">
              {data.ordini.da_ritirare.slice(0, 5).map((o) => (
                <li key={o.id} className="text-sm flex items-center gap-2" data-testid={`arrived-order-${o.id}`}>
                  <span className="font-semibold shrink-0">{o.client_name}</span>
                  <span className="text-muted-foreground shrink-0">—</span>
                  <span className="flex-1 min-w-0 truncate text-xs text-muted-foreground">{o.article}</span>
                  {o.whatsapp_notified_at
                    ? <span className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-500 text-[10px] font-bold uppercase tracking-wider" data-testid={`arrived-notified-${o.id}`}><WhatsappLogo size={11} weight="fill" /> Avvisato</span>
                    : <span className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 text-[10px] font-bold uppercase tracking-wider blink-red" data-testid={`arrived-not-notified-${o.id}`}>Da avvisare</span>}
                </li>
              ))}
            </ul>
            <Link to="/ordini" data-testid="go-to-orders-link" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-amber-500 hover:text-amber-400 transition-colors duration-150">
              Vai agli ordini <ArrowRight size={14} />
            </Link>
          </div>
        )}
        <div className={`border border-border rounded-lg bg-card p-3 ${data.ordini.da_ritirare.length === 0 ? "xl:col-start-2" : ""}`} data-testid="dashboard-notes">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
              <NotePencil size={13} /> Note — spazio libero
            </p>
            {notesSaved && <span className="text-[10px] font-semibold text-green-500" data-testid="dashboard-notes-saved">Salvato</span>}
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            data-testid="dashboard-notes-input"
            placeholder="Appuntati qualsiasi cosa: resta qui, senza nulla a pretendere..."
            className="w-full px-3 py-2 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150 resize-y" />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
              <Wrench size={20} weight="duotone" /> Riparazioni in sospeso ({data.riparazioni.attive.length})
            </h2>
            <Link to="/riparazioni" data-testid="go-to-repairs-link" className="text-sm font-semibold text-primary hover:opacity-80 inline-flex items-center gap-1.5 transition-opacity duration-150">
              Tutte <ArrowRight size={14} />
            </Link>
          </div>
          {data.riparazioni.attive.length === 0 ? (
            <div className="border border-border rounded-lg bg-card p-10 text-center text-muted-foreground text-sm" data-testid="no-active-repairs">
              Nessuna riparazione in sospeso
            </div>
          ) : (
            <div className="space-y-3" data-testid="pending-repairs-list">
              {data.riparazioni.attive.map((r) => {
                const days = daysSince(r.created_at);
                const overdue = days >= 2;
                const isOpen = expanded === r.id;
                return (
                  <div key={r.id}
                    onClick={() => toggle(r.id)}
                    data-testid={`pending-repair-${r.id}`}
                    className={`border rounded-lg bg-card cursor-pointer transition-transform transition-colors duration-200 hover:-translate-y-0.5 border-l-4 ${
                      overdue ? "border-l-red-500 border-red-500/40" : "border-l-amber-500 border-border"
                    } ${isOpen ? "ring-1 ring-primary" : ""}`}>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2.5">
                        <div className={`w-12 h-12 rounded-md flex flex-col items-center justify-center shrink-0 px-1 text-center leading-tight ${
                          days > 7 ? "bg-red-500/15 text-red-500" : overdue ? "bg-amber-500/15 text-amber-500" : "bg-primary/15 text-primary"}`}
                          data-testid={`repair-days-${r.id}`}>
                          <span className="text-base font-extrabold leading-none">{days}</span>
                          <span className="text-[8px] font-bold uppercase tracking-wide">{days === 1 ? "giorno" : "giorni"}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-bold truncate">{r.client_name} — {r.device_label}</p>
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                            {r.problem} · <b className="text-foreground">{r.technician_name}</b>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <CaretDown size={16} className={`text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-3 border-t border-border grid grid-cols-2 gap-x-4 gap-y-3" data-testid={`repair-details-${r.id}`}>
                        <div className="col-span-2 flex justify-end -mb-1">
                          <Link to={`/riparazioni?apri=${r.id}`} onClick={(e) => e.stopPropagation()} data-testid={`goto-repair-${r.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:opacity-80 transition-opacity duration-150">
                            Apri la pratica <ArrowRight size={13} />
                          </Link>
                        </div>
                        <Detail icon={Phone} label="Telefono" value={r.client_phone} />
                        <Detail icon={User} label="Tecnico" value={r.technician_name} />
                        <Detail icon={CalendarBlank} label="Aperta il" value={fmtDate(r.created_at)} />
                        <Detail icon={CalendarBlank} label="Consegna stimata" value={r.estimated_delivery || "—"} />
                        <Detail icon={Tag} label="Prezzo" value={r.price != null ? eur(r.price) : "—"} />
                        <Detail
                          icon={r.no_password ? LockSimpleOpen : LockSimple}
                          label="Sblocco"
                          value={r.no_password ? "Senza password" : `Codice: ${r.unlock_password || "—"}`}
                        />
                        <div className="col-span-2">
                          <Detail icon={NotePencil} label="Problema" value={r.problem} />
                        </div>
                        {r.notes && (
                          <div className="col-span-2">
                            <Detail icon={NotePencil} label="Note" value={r.notes} />
                          </div>
                        )}
                        {(r.accessories || []).length > 0 && (
                          <div className="col-span-2 flex flex-wrap gap-1.5">
                            {r.accessories.map((a) => (
                              <span key={a} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-[10px] font-semibold">{a}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
              <Package size={20} weight="duotone" /> Ordini da evadere ({data.ordini.da_evadere.length})
            </h2>
            <Link to="/ordini" data-testid="go-to-orders-link-2" className="text-sm font-semibold text-primary hover:opacity-80 inline-flex items-center gap-1.5 transition-opacity duration-150">
              Tutti <ArrowRight size={14} />
            </Link>
          </div>
          {data.ordini.da_evadere.length === 0 ? (
            <div className="border border-border rounded-lg bg-card p-10 text-center text-muted-foreground text-sm" data-testid="no-pending-orders">
              Nessun ordine da evadere
            </div>
          ) : (
            <div className="space-y-3" data-testid="pending-orders-list">
              {data.ordini.da_evadere.map((o) => {
                const days = daysSince(o.created_at);
                const overdue = days >= 2;
                const isOpen = expanded === o.id;
                return (
                  <div key={o.id}
                    onClick={() => toggle(o.id)}
                    data-testid={`pending-order-${o.id}`}
                    className={`border rounded-lg bg-card cursor-pointer transition-transform transition-colors duration-200 hover:-translate-y-0.5 border-l-4 ${
                      overdue ? "border-l-red-500 border-red-500/40" : "border-l-blue-500 border-border"
                    } ${isOpen ? "ring-1 ring-primary" : ""}`}>
                    <div className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-md flex flex-col items-center justify-center shrink-0 px-1 text-center leading-tight ${
                          days > 7 ? "bg-red-500/15 text-red-500" : overdue ? "bg-amber-500/15 text-amber-500" : "bg-blue-500/15 text-blue-400"}`}
                          data-testid={`order-days-${o.id}`}>
                          <span className="text-base font-extrabold leading-none">{days}</span>
                          <span className="text-[8px] font-bold uppercase tracking-wide">{days === 1 ? "giorno" : "giorni"}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold truncate">{o.article}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {o.client_name}{o.client_phone ? ` · ${o.client_phone}` : ""}
                            {o.assignee_name ? ` · ` : ""}
                            {o.assignee_name && <b className="text-primary">→ {o.assignee_name}</b>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <CaretDown size={16} className={`text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-3 border-t border-border grid grid-cols-2 gap-x-4 gap-y-3" data-testid={`order-details-${o.id}`}>
                        <div className="col-span-2 flex justify-end -mb-1">
                          <Link to={`/ordini?apri=${o.id}`} onClick={(e) => e.stopPropagation()} data-testid={`goto-order-${o.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:opacity-80 transition-opacity duration-150">
                            Apri l'ordine <ArrowRight size={13} />
                          </Link>
                        </div>
                        <Detail icon={User} label="Cliente" value={o.client_name} />
                        <Detail icon={Phone} label="Telefono" value={o.client_phone || "—"} />
                        <Detail icon={CalendarBlank} label="Annotato il" value={fmtDate(o.created_at)} />
                        <Detail icon={User} label="Assegnato a" value={o.assignee_name || "—"} />
                        <Detail icon={Tag} label="Prezzo" value={o.price != null ? eur(o.price) : "—"} />
                        <Detail icon={User} label="Annotato da" value={o.created_by} />
                        <div className="col-span-2">
                          <Detail icon={Package} label="Articolo" value={o.article} />
                        </div>
                        {o.notes && (
                          <div className="col-span-2">
                            <Detail icon={NotePencil} label="Nota completa" value={o.notes} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {(doneRepairs.length > 0 || doneOrders.length > 0) && (
        <div data-testid="done-today-section">
          <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2 mb-4">
            <CheckCircle size={20} weight="duotone" className="text-green-500" /> Fatte oggi
          </h2>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-3">
              {doneRepairs.map((r) => (
                <div key={r.id} className="border border-border border-l-4 border-l-green-500 rounded-lg bg-card p-4" data-testid={`done-repair-${r.id}`}>
                  <p className="text-sm font-bold">{r.client_name} — {r.device_label}</p>
                  {r.resolution && <p className="text-xs text-muted-foreground mt-1">Intervento: {r.resolution}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    {r.technician_name}{r.price != null ? ` · ${eur(r.price)}` : ""}
                  </p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {doneOrders.map((o) => (
                <div key={o.id} className="border border-border border-l-4 border-l-green-500 rounded-lg bg-card p-4" data-testid={`done-order-${o.id}`}>
                  <p className="text-sm font-bold">{o.article}</p>
                  <p className="text-xs text-muted-foreground mt-1">Ritirato da {o.client_name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
